import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getFullImageUrl, handleImageError } from '../utils/imageUtils';

interface PageProject {
  _id?: string; // Optional for temporary use in frontend only
  name: string;
  imageUrl: string;
  link: string;
  addedAt: string;
  status?: string;
}

interface StakePage {
  _id: string;
  pageUrl: string;
  title: string;
  subtitle: string;
  description: string;
  projects: PageProject[];
  isPublished: boolean;
  createdAt: string;
}

interface StakePageFormData {
  pageUrl: string;
  title: string;
  subtitle: string;
  description: string;
  isPublished: boolean;
}

const CreateLikeStake: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingPage, setEditingPage] = useState<StakePage | null>(null);
  const [stakePages, setStakePages] = useState<StakePage[]>([]);
  
  // States for project management
  const [formProjects, setFormProjects] = useState<PageProject[]>([]);
  const [newProject, setNewProject] = useState({
    name: '',
    imageUrl: '',
    link: '',
    status: ''
  });
  
  // States for auto-detection
  const [existingProjects, setExistingProjects] = useState<any[]>([]);
  const [projectsLoading, setProjectsLoading] = useState(true);

  const [formData, setFormData] = useState<StakePageFormData>({
    pageUrl: '',
    title: '',
    subtitle: '',
    description: '',
    isPublished: true
  });

  // Функція для обробки зміни URL сторінки
  const handlePageUrlChange = (newUrl: string) => {
    // Автоматично форматуємо URL
    let formattedUrl = newUrl.toLowerCase().replace(/[^a-z0-9/]/g, '-').replace(/-+/g, '-');
    if (formattedUrl && !formattedUrl.startsWith('/')) {
      formattedUrl = '/' + formattedUrl;
    }
    setFormData(prev => ({ ...prev, pageUrl: formattedUrl }));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (name === 'pageUrl') {
      handlePageUrlChange(value);
    } else if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // Функція для завантаження сторінок з API
  const loadPagesFromAPI = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || window.location.origin;
      
      console.log('🔄 Loading pages from API:', apiUrl);
      
      // httpOnly cookies are sent automatically - no need to get token
      const response = await axios.get(`${apiUrl}/api/custom-pages/admin-all`);
      
      console.log('📡 API Response:', response.data);
        
      if (response.data.success && response.data.data) {
        console.log('✅ Loaded pages from API:', response.data.data.length);
        setStakePages(response.data.data);
        // Зберігаємо в localStorage як backup
        localStorage.setItem('stakePages', JSON.stringify(response.data.data));
        console.log('💾 Saved to localStorage backup');
        return;
      } else {
        console.warn('⚠️ API response not successful, checking localStorage');
      }
    } catch (error: any) {
      console.error('❌ API error:', error.message);
      console.error('❌ Status:', error.response?.status);
      console.error('❌ Data:', error.response?.data);
    }
    
    // Fallback до localStorage тільки якщо API не доступна
    console.log('📚 Using localStorage fallback');
    const savedPages = localStorage.getItem('stakePages');
    if (savedPages) {
      try {
        const pages = JSON.parse(savedPages);
        setStakePages(pages);
        console.log('✅ Loaded pages from localStorage:', pages.length);
      } catch (e) {
        console.error('❌ Error parsing localStorage:', e);
        setStakePages([]);
      }
    } else {
      console.log('⚠️ No localStorage data available');
      setStakePages([]);
    }
  };

  // Завантаження сторінок
  useEffect(() => {
    console.log('🚀 Component mounted, loading pages');
    loadPagesFromAPI();
    
    // Також оновлюємо кожні 30 секунд для синхронізації з сервером
    const refreshInterval = setInterval(() => {
      console.log('🔄 Refreshing pages from server');
      loadPagesFromAPI();
    }, 30000);
    
    return () => clearInterval(refreshInterval);
  }, []);

  // Завантажуємо існуючі проекти для автозаповнення
  useEffect(() => {
    const fetchExistingProjects = async () => {
      setProjectsLoading(true);
      try {
        const apiUrl = process.env.REACT_APP_API_URL || window.location.origin;
        let allProjects: any[] = [];
        
        console.log('🔄 Starting to fetch projects from:', apiUrl);
        
        // Завантажуємо опубліковані проекти
        console.log('📡 Fetching public projects...');
        const publicResponse = await axios.get(`${apiUrl}/api/projects`);
        console.log('📡 Public API response:', publicResponse.data);
        
        if (publicResponse.data.success && publicResponse.data.data) {
          if (publicResponse.data.data.projects) {
            allProjects.push(...publicResponse.data.data.projects);
            console.log('✅ Public projects loaded:', publicResponse.data.data.projects.length);
          } else {
            console.log('⚠️ Public response has no projects array');
          }
        } else {
          console.log('⚠️ Public response not successful or no data');
        }
        
        // Завантажуємо приватні проекти (потребує токен)
        // Fetch admin-only projects using httpOnly cookies
        console.log('📡 Fetching admin-only projects...');
        try {
          const adminOnlyResponse = await axios.get(`${apiUrl}/api/projects/admin`);
          console.log('📡 Admin API response:', adminOnlyResponse.data);
          
          if (adminOnlyResponse.data.success && adminOnlyResponse.data.data) {
            if (adminOnlyResponse.data.data.projects) {
              allProjects.push(...adminOnlyResponse.data.data.projects);
              console.log('✅ Admin Only projects loaded:', adminOnlyResponse.data.data.projects.length);
            } else {
              console.log('⚠️ Admin response has no projects array');
            }
          } else {
            console.log('⚠️ Admin response not successful or no data');
          }
        } catch (adminError: any) {
          console.error('❌ Error fetching admin projects:', adminError);
        }
        
        console.log('📊 Total projects collected:', allProjects.length);
        
        if (allProjects.length > 0) {
          setExistingProjects(allProjects);
          console.log('🎉 Total projects loaded:', allProjects.length);
          console.log('📋 Available projects for auto-detection:');
          allProjects.forEach((p: any, index) => {
            console.log(`  ${index + 1}. "${p.title}" (${p.category})`);
          });
        } else {
          console.log('❌ No projects loaded from either endpoint');
        }
      } catch (error: any) {
        console.error('❌ Error fetching existing projects:', error);
        if (error.response) {
          console.error('❌ Error response:', error.response.data);
          console.error('❌ Error status:', error.response.status);
        }
      } finally {
        setProjectsLoading(false);
      }
    };

    fetchExistingProjects();
  }, []);

  const saveToLocalStorage = (data: StakePage[]) => {
    localStorage.setItem('stakePages', JSON.stringify(data));
  };

  const toggleForm = () => {
    setShowForm(!showForm);
    if (showForm) {
      setEditingPage(null);
      clearForm();
    }
  };

  const clearForm = () => {
    setFormData({
      pageUrl: '',
      title: '',
      subtitle: '',
      description: '',
      isPublished: true
    });
    setFormProjects([]);
    setNewProject({
      name: '',
      imageUrl: '',
      link: '',
      status: ''
    });
  };

  const handleEditPage = (page: StakePage) => {
    setEditingPage(page);
    setFormData({
      pageUrl: page.pageUrl,
      title: page.title,
      subtitle: page.subtitle,
      description: page.description,
      isPublished: page.isPublished
    });
    setFormProjects(page.projects || []);
    setShowForm(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      console.log('📝 handleSubmit called');
      if (!formData.pageUrl.trim()) {
        alert('Page URL is required');
        return;
      }

      if (!formData.title.trim()) {
        alert('Page title is required');
        return;
      }

      // Перевіряємо на дублікат URL (але не для редагування існуючої сторінки)
      if (!editingPage) {
        const urlExists = stakePages.some(page => page.pageUrl === formData.pageUrl);
        if (urlExists) {
          alert(`❌ Page URL "${formData.pageUrl}" already exists. Please use a different URL.`);
          return;
        }
      }

      const pageData = {
        pageUrl: formData.pageUrl,
        title: formData.title,
        subtitle: formData.subtitle,
        description: formData.description,
        projects: formProjects,
        isPublished: formData.isPublished
      };

      console.log('📤 Sending pageData:', pageData);

      const apiUrl = process.env.REACT_APP_API_URL || window.location.origin;

      console.log('🌐 API URL:', apiUrl);
      console.log('🔑 Using httpOnly cookies for authentication');

      // Спробуємо зберегти через API
      try {
        let response;
        if (editingPage) {
          // Редагування існуючої сторінки
          console.log('✏️ Updating existing page:', editingPage._id);
          response = await axios.put(`${apiUrl}/api/custom-pages/${editingPage._id}`, pageData);
          console.log('✅ Updated page via API:', formData.pageUrl);
        } else {
          // Створення нової сторінки
          console.log('➕ Creating new page');
          response = await axios.post(`${apiUrl}/api/custom-pages`, pageData);
          console.log('✅ Created new page via API:', formData.pageUrl);
          console.log('📊 Response:', response.data);
        }

        if (response.data.success) {
          console.log('🎉 API success response');
          // Перезавантажуємо сторінки з сервера
          try {
            const reloadResponse = await axios.get(`${apiUrl}/api/custom-pages/admin-all`);
            if (reloadResponse.data.success) {
              setStakePages(reloadResponse.data.data);
              console.log('🔄 Reloaded pages:', reloadResponse.data.data.length);
            }
          } catch (reloadError) {
            console.log('⚠️ Error reloading pages, adding locally');
            // Навіть якщо не можемо завантажити з сервера, додаємо локально
            let updatedPages;
            if (editingPage) {
              updatedPages = stakePages.map(page => 
                page._id === editingPage._id 
                  ? { ...pageData, _id: editingPage._id, createdAt: editingPage.createdAt }
                  : page
              );
            } else {
              const newPage = {
                ...pageData,
                _id: response.data.data?._id || `temp_page_${Date.now()}_${Math.random()}`,
                createdAt: response.data.data?.createdAt || new Date().toISOString()
              };
              updatedPages = [...stakePages, newPage];
            }
            setStakePages(updatedPages);
          }
          alert(`Page ${editingPage ? 'updated' : 'created'} successfully! Available at: ${formData.pageUrl}`);
        }
      } catch (apiError: any) {
        console.error('❌ API error:', apiError);
        console.error('❌ Error response:', apiError.response?.data);
        console.error('❌ Error message:', apiError.message);
        console.error('❌ Error status:', apiError.response?.status);
        
        // Перевіряємо, чи це помилка дублікату
        const isDuplicateError = apiError.response?.status === 400 && 
                                  apiError.response?.data?.message === 'Page URL already exists';
        
        if (isDuplicateError) {
          console.log('⚠️ Page URL already exists - reloading data from server');
          alert(`❌ Error: Page URL "${formData.pageUrl}" already exists. Loading existing pages...`);
          // Перезавантажуємо дані щоб показати існуючу сторінку
          await loadPagesFromAPI();
          console.log('✅ Reloaded pages after duplicate error');
          return;
        }
        
        // Fallback до localStorage для інших помилок
        console.log('⚠️ Falling back to localStorage');
        let updatedPages;
        if (editingPage) {
          updatedPages = stakePages.map(page => 
            page._id === editingPage._id 
              ? { ...pageData, _id: editingPage._id, createdAt: editingPage.createdAt }
              : page
          );
        } else {
          const newPage = {
            ...pageData,
            _id: `temp_page_${Date.now()}_${Math.random()}`,
            createdAt: new Date().toISOString()
          };
          updatedPages = [...stakePages, newPage];
        }

        setStakePages(updatedPages);
        saveToLocalStorage(updatedPages);
        console.log('✅ Saved to localStorage as fallback');
        alert(`Page ${editingPage ? 'updated' : 'created'} successfully! Available at: ${formData.pageUrl}`);
      }

      // Очищення форми
      clearForm();
      setEditingPage(null);
      setShowForm(false);
      
      // Явно перезавантажуємо дані після успішного збереження
      setTimeout(() => {
        console.log('⏱️ Reloading pages after 500ms');
        loadPagesFromAPI();
      }, 500);

    } catch (error) {
      console.error('❌ Outer catch error:', error);
      alert('Error saving page: ' + (error instanceof Error ? error.message : 'Unknown error'));
      alert('Error saving page');
    }
  };

  const handleDeletePage = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this page?')) {
      return;
    }

    try {
      const apiUrl = process.env.REACT_APP_API_URL || window.location.origin;

      // Спробуємо видалити через API
      try {
        const response = await axios.delete(`${apiUrl}/api/custom-pages/${id}`);
        
        if (response.data.success) {
          try {
            const reloadResponse = await axios.get(`${apiUrl}/api/custom-pages/admin-all`);
            if (reloadResponse.data.success) {
              setStakePages(reloadResponse.data.data);
            }
          } catch (reloadError) {
            console.log('Error reloading pages, using current state');
          }
          console.log('✅ Deleted page via API');
          return;
        }
      } catch (apiError) {
        console.error('API error, falling back to localStorage:', apiError);
      }

      // Fallback до localStorage
      const updatedPages = stakePages.filter(page => page._id !== id);
      setStakePages(updatedPages);
      saveToLocalStorage(updatedPages);
      console.log('✅ Deleted page from localStorage as fallback');
    } catch (error) {
      console.error('Error deleting page:', error);
      alert('Error deleting page');
    }
  };

  // Project management functions
  const addProject = () => {
    if (!newProject.name.trim()) {
      alert('Project name is required');
      return;
    }

    const project: PageProject = {
      name: newProject.name.trim(),
      imageUrl: newProject.imageUrl.trim(),
      link: newProject.link.trim(),
      addedAt: new Date().toISOString(),
      status: newProject.status ? newProject.status.trim() : undefined
    };

    setFormProjects(prev => [...prev, project]);
    setNewProject({
      name: '',
      imageUrl: '',
      link: '',
      status: ''
    });
  };

  const removeProject = (projectIndex: number) => {
    setFormProjects(prev => prev.filter((_, index) => index !== projectIndex));
  };

  // Функція для конвертації відносних URL в повні
  const convertToFullUrl = (url: string) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    const baseUrl = process.env.REACT_APP_API_URL || window.location.origin;
    return url.startsWith('/') ? baseUrl + url : baseUrl + '/' + url;
  };

  // Функція для пошуку проекту за назвою (точний збіг)
  const findProjectData = (projectName: string) => {
    if (!projectName.trim()) {
      console.log('🔍 Empty project name');
      return null;
    }
    
    if (!existingProjects.length) {
      console.log('🔍 No existing projects loaded yet, total projects:', existingProjects.length);
      return null;
    }
    
    console.log('🔍 Searching for project data:', `"${projectName}"`, 'in', existingProjects.length, 'total projects');
    console.log('🔍 Available project titles:', existingProjects.map(p => `"${p.title}"`));
    
    // Шукаємо проект з точною назвою (без урахування регістру)
    const matchingProject = existingProjects.find(project => 
      project.title && project.title.toLowerCase() === projectName.toLowerCase()
    );
    
    if (matchingProject) {
      console.log('🎯 Exact match found for:', `"${projectName}"`, '→', `"${matchingProject.title}"`, '(category:', matchingProject.category + ')');
      console.log('🎯 Matched project data:', {
        title: matchingProject.title,
        imageUrl: matchingProject.imageUrl,
        category: matchingProject.category,
        links: matchingProject.links
      });
      return matchingProject;
    }
    
    console.log('❌ No exact match for:', `"${projectName}"`);
    console.log('❌ Tried to match against:', existingProjects.map(p => `"${p.title}"`));
    return null;
  };

  // Обробник зміни назви проекту з автозаповненням
  const handleProjectNameChange = (newName: string) => {
    const projectData = findProjectData(newName);
    
    if (projectData) {
      // Автозаповнення полів з знайденого проекту
      console.log('🔄 Auto-filling project fields from:', projectData.title);
      setNewProject(prev => ({
        ...prev,
        name: newName,
        imageUrl: projectData.imageUrl ? convertToFullUrl(projectData.imageUrl) : prev.imageUrl,
        link: projectData.links?.website || projectData.projectUrl || prev.link
      }));
    } else {
      // Просто оновлюємо назву
      setNewProject(prev => ({
        ...prev,
        name: newName
      }));
    }
  };

  const handleNewProjectChange = (field: string, value: string) => {
    if (field === 'name') {
      handleProjectNameChange(value);
    } else {
      setNewProject(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  // Фільтруємо Stake Pages
  const filteredStakePages = stakePages.filter(page =>
    (page.pageUrl && page.pageUrl.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (page.title && page.title.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (page.subtitle && page.subtitle.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div className="text-gray-100 font-sans h-full" style={{backgroundColor: '#0a0908', padding: '2rem'}}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-dusk-amber to-dusk-copper bg-clip-text text-transparent mb-2">Page Management</h1>
            <p className="text-gray-400">Create and manage custom pages with project listings</p>
          </div>
          
          <div className="flex items-center space-x-4">
            <div className="relative">
              <input
                type="text"
                placeholder="Search pages..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-3 bg-[rgba(243,235,226,0.04)] border border-[var(--dusk-line)] rounded-xl text-white placeholder-[var(--dusk-faint)] focus:outline-none focus:ring-2 focus:ring-dusk-copper/50 focus:border-dusk-copper transition-all"
              />
              <svg className="absolute left-3 top-3.5 h-5 w-5 text-[var(--dusk-faint)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>

            <button
              onClick={toggleForm}
              className="px-6 py-3 bg-gradient-to-r from-dusk-copper to-dusk-amber hover:from-dusk-copper-deep hover:to-dusk-copper text-white font-semibold rounded-xl transition-all shadow-lg hover:shadow-dusk-copper/40"
            >
              {showForm ? (
                <>
                  <svg className="w-5 h-5 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Cancel
                </>
              ) : (
                <>
                  <svg className="w-5 h-5 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Add Page
                </>
              )}
            </button>
          </div>
        </div>

        {/* Form */}
        {showForm && (
          <div className="p-6 mb-8 rounded-2xl border border-[var(--dusk-line)] bg-[rgba(243,235,226,0.04)] backdrop-blur-sm">
            <h2 className="text-xl font-bold text-white mb-6">
              {editingPage ? 'Edit Page' : 'Create New Page'}
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Page URL */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Page URL</label>
                <input
                  type="text"
                  name="pageUrl"
                  value={formData.pageUrl}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 bg-[rgba(243,235,226,0.06)] border border-[var(--dusk-line-strong)] rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-dusk-copper"
                  placeholder="/stakes"
                  required
                />
                <p className="text-xs text-gray-400 mt-1">URL for the page (e.g., /stakes, /defi, /gaming)</p>
              </div>

              {/* Page Content */}
              <div className="border-t border-[var(--dusk-line-strong)] pt-6">
                <h4 className="text-lg font-medium text-white mb-4">Page Content</h4>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Title *</label>
                    <input
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 bg-[rgba(243,235,226,0.06)] border border-[var(--dusk-line-strong)] rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-dusk-copper"
                      placeholder="Stake the future."
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Subtitle</label>
                    <input
                      type="text"
                      name="subtitle"
                      value={formData.subtitle}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 bg-[rgba(243,235,226,0.06)] border border-[var(--dusk-line-strong)] rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-dusk-copper"
                      placeholder="Join DUSKPROOF — where decentralization meets automation."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Description</label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      rows={3}
                      className="w-full px-4 py-2 bg-[rgba(243,235,226,0.06)] border border-[var(--dusk-line-strong)] rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-dusk-copper"
                      placeholder="We build, monitor, and scale validators across ecosystems — turning infrastructure into opportunity."
                    />
                  </div>
                </div>
              </div>

              {/* Page Projects Management */}
              <div className="border-t border-[var(--dusk-line-strong)] pt-6">
                <h4 className="text-lg font-medium text-white mb-4">Page Projects</h4>
                
                {/* Add New Project */}
                <div className="mb-6">
                  <h5 className="text-sm font-medium text-gray-300 mb-3">Add New Project</h5>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-400 mb-1">
                        Project Name *
                        {newProject.imageUrl && findProjectData(newProject.name) && (
                          <span className="ml-2 text-xs text-green-400">✓ Auto-detected</span>
                        )}
                      </label>
                      <input
                        type="text"
                        value={newProject.name}
                        onChange={(e) => handleNewProjectChange('name', e.target.value)}
                        className="w-full px-3 py-2 bg-[rgba(243,235,226,0.06)] border border-[var(--dusk-line-strong)] rounded text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-dusk-copper text-sm"
                        placeholder="e.g., Starknet (auto-detects image & link)"
                      />
                      <p className="text-xs text-[var(--dusk-faint)] mt-1">
                        {projectsLoading ? (
                          "🔄 Loading existing projects..."
                        ) : existingProjects.length > 0 ? (
                          `💡 Auto-detection available (${existingProjects.length} projects loaded)`
                        ) : (
                          "💡 No existing projects found for auto-detection"
                        )}
                      </p>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-400 mb-1">Image URL</label>
                      <input
                        type="text"
                        value={newProject.imageUrl}
                        onChange={(e) => handleNewProjectChange('imageUrl', e.target.value)}
                        className="w-full px-3 py-2 bg-[rgba(243,235,226,0.06)] border border-[var(--dusk-line-strong)] rounded text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-dusk-copper text-sm"
                        placeholder="https://example.com/image.png"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-400 mb-1">Project Link</label>
                      <input
                        type="text"
                        value={newProject.link}
                        onChange={(e) => handleNewProjectChange('link', e.target.value)}
                        className="w-full px-3 py-2 bg-[rgba(243,235,226,0.06)] border border-[var(--dusk-line-strong)] rounded text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-dusk-copper text-sm"
                        placeholder="https://project-website.com"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-400 mb-1">Status (APY, Soon, etc.)</label>
                      <input
                        type="text"
                        value={newProject.status || ''}
                        onChange={(e) => handleNewProjectChange('status', e.target.value)}
                        className="w-full px-3 py-2 bg-[rgba(243,235,226,0.06)] border border-[var(--dusk-line-strong)] rounded text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-dusk-copper text-sm"
                        placeholder="e.g., -9.25% or Soon"
                      />
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={addProject}
                    className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded transition-colors"
                  >
                    + Add Project
                  </button>
                </div>

                {/* Current Projects List */}
                {formProjects.length > 0 && (
                  <div className="mb-6">
                    <h5 className="text-sm font-medium text-gray-300 mb-3">
                      Current Projects ({formProjects.length})
                    </h5>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {formProjects.map((project: PageProject, index: number) => (
                        <div key={project.addedAt ? `${project.name}-${project.addedAt}` : `project-${index}`} className="bg-[rgba(243,235,226,0.06)] rounded-lg p-4 border border-[var(--dusk-line-strong)]">
                          <div className="flex items-start justify-between">
                            <div className="flex items-start space-x-3 flex-1">
                              <div className="flex-shrink-0">
                                {project.imageUrl ? (
                                  <img
                                    src={project.imageUrl}
                                    alt={project.name}
                                    className="w-12 h-12 rounded-lg object-cover border border-[var(--dusk-line-strong)]"
                                    onError={(e) => {
                                      const target = e.target as HTMLImageElement;
                                      const parent = target.parentElement;
                                      target.style.display = 'none';
                                      // Створюємо fallback div якщо зображення не завантажилось
                                      const fallbackDiv = document.createElement('div');
                                      fallbackDiv.className = 'w-12 h-12 rounded-lg bg-gray-600 flex items-center justify-center';
                                      fallbackDiv.innerHTML = `<svg class="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg>`;
                                      parent?.appendChild(fallbackDiv);
                                    }}
                                  />
                                ) : (
                                  <div className="w-12 h-12 rounded-lg bg-gray-600 flex items-center justify-center">
                                    <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                    </svg>
                                  </div>
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <h6 className="text-white font-medium text-sm truncate">{project.name}</h6>
                                {project.status && (
                                  <p className="text-gray-400 text-xs mt-1">{project.status}</p>
                                )}
                                {project.link && (
                                  <a
                                    href={project.link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-dusk-amber hover:text-dusk-amber text-xs truncate block mt-1"
                                  >
                                    {project.link}
                                  </a>
                                )}
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeProject(index)}
                              className="ml-2 p-1 text-red-400 hover:text-red-300 transition-colors"
                              title="Remove project"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Visibility */}
              <div className="border-t border-[var(--dusk-line-strong)] pt-6">
                <div className="flex items-center space-x-6">
                  <span className="text-sm font-medium text-gray-300">Visibility:</span>
                  <div className="flex items-center">
                    <input
                      type="radio"
                      id="adminOnly"
                      name="visibility"
                      checked={!formData.isPublished}
                      onChange={() => setFormData({ ...formData, isPublished: false })}
                      className="mr-2"
                    />
                    <label htmlFor="adminOnly" className="text-sm font-medium text-gray-300 mr-4">
                      Admin Only
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="radio"
                      id="public"
                      name="visibility"
                      checked={formData.isPublished}
                      onChange={() => setFormData({ ...formData, isPublished: true })}
                      className="mr-2"
                    />
                    <label htmlFor="public" className="text-sm font-medium text-gray-300">
                      Public
                    </label>
                  </div>
                </div>
              </div>

              {/* Form Actions */}
              <div className="flex space-x-4 pt-6 border-t border-[var(--dusk-line-strong)]">
                <button
                  type="submit"
                  className="px-6 py-3 bg-gradient-to-r from-teal-700 to-cyan-700 hover:from-teal-600 hover:to-cyan-600 text-white font-semibold rounded-xl transition-all shadow-lg hover:shadow-teal-500/30"
                >
                  {editingPage ? 'Update Page' : 'Create Page'}
                </button>
                <button
                  type="button"
                  onClick={toggleForm}
                  className="px-6 py-3 bg-[rgba(243,235,226,0.06)]/50 hover:bg-gray-600/50 text-gray-200 font-semibold rounded-xl transition-all"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Pages Table */}
        <div className="rounded-2xl border border-[var(--dusk-line)] overflow-hidden bg-[rgba(243,235,226,0.03)] backdrop-blur-sm">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[rgba(243,235,226,0.05)] border-b border-[var(--dusk-line)]">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                    Page
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                    Projects
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-gray-300 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700/30">
                {filteredStakePages.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-6 py-8 text-center">
                      <div className="text-gray-400">
                        <svg className="mx-auto h-12 w-12 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                        <p className="text-lg font-medium">
                          {stakePages.length === 0 ? 'No pages yet' : 'No pages found'}
                        </p>
                        <p className="text-sm">
                          {stakePages.length === 0 ? 'Click "Add Page" to create your first page' : 'Try adjusting your search terms'}
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredStakePages.map((page: StakePage) => (
                    <tr key={page._id} className="hover:bg-[rgba(243,235,226,0.05)] transition-all">
                      <td className="px-6 py-4">
                        <div className="flex items-start">
                          <div className="flex-shrink-0 h-10 w-10 bg-gray-600 rounded-lg flex items-center justify-center">
                            <svg className="w-6 h-6 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                            </svg>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-dusk-amber">
                              {page.pageUrl}
                            </div>
                            <div className="text-sm font-medium text-white mt-1">
                              {page.title || 'No title'}
                            </div>
                            {page.subtitle && (
                              <div className="text-sm text-gray-400 mt-1">
                                {page.subtitle}
                              </div>
                            )}
                            {page.description && (
                              <div className="text-sm text-[var(--dusk-faint)] mt-1 max-w-md truncate">
                                {page.description}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-300">
                          {page.projects?.length || 0} projects
                        </div>
                        {page.projects && page.projects.length > 0 && (
                          <div className="flex -space-x-2 mt-1">
                            {page.projects.slice(0, 3).map((project, index) => (
                              <div key={project._id || `project-${index}`} className="relative">
                                {project.imageUrl ? (
                                  <img
                                    src={getFullImageUrl(project.imageUrl)}
                                    alt={project.name}
                                    className="w-6 h-6 rounded-full border-2 border-gray-800 object-cover"
                                    title={project.name}
                                    onError={handleImageError}
                                  />
                                ) : (
                                  <div 
                                    className="w-6 h-6 rounded-full border-2 border-gray-800 bg-gray-600 flex items-center justify-center"
                                    title={project.name}
                                  >
                                    <span className="text-xs text-white">{project.name.charAt(0).toUpperCase()}</span>
                                  </div>
                                )}
                              </div>
                            ))}
                            {page.projects.length > 3 && (
                              <div className="w-6 h-6 rounded-full border-2 border-gray-800 bg-gray-600 flex items-center justify-center">
                                <span className="text-xs text-white">+{page.projects.length - 3}</span>
                              </div>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          page.isPublished ? 'bg-green-800 text-green-100' : 'bg-red-800 text-red-100'
                        }`}>
                          {page.isPublished ? 'Public' : 'Admin Only'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => {
                              const pageUrl = page.pageUrl.startsWith('/') ? page.pageUrl.substring(1) : page.pageUrl;
                              window.open(`/${pageUrl}`, '_blank');
                            }}
                            className="p-2 text-dusk-amber hover:text-dusk-amber hover:bg-[rgba(208,0,104,0.15)] rounded-lg transition-all"
                            title="View Page"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleEditPage(page)}
                            className="p-2 text-yellow-400 hover:text-yellow-300 hover:bg-yellow-500/20 rounded-lg transition-all"
                            title="Edit Page"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleDeletePage(page._id)}
                            className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/20 rounded-lg transition-all"
                            title="Delete Page"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateLikeStake;
